import React, { useState } from 'react';
import LaunchModal from '../components/LaunchModal';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <div className="bg-gray-950 text-white antialiased">
      {/* Hero Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            Built by Horse People,
            <span className="brand-text-gradient"> for Horse People</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            BarnBills was born from real frustration with outdated horse farm software. 
            We're creating the modern, intuitive tools that the equestrian industry deserves.
          </p>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>Founded in 2024</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>Kentucky-based</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>Industry expertise</span>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Our Story</h2>
              <div className="space-y-6 text-gray-400">
                <p>
                  BarnBills was born from real-world frustration. After years of watching horse farm operations 
                  struggle with outdated, clunky software that seemed designed by people who had never set foot 
                  in a barn, we knew there had to be a better way.
                </p>
                <p>
                  Traditional horse farm management software felt like it was built in the 1990s and never updated. 
                  Complex interfaces, poor mobile experiences, and features that seemed to miss the mark entirely. 
                  Farm owners deserved better.
                </p>
                <p>
                  So we set out to build something different. Software that actually understands how modern horse 
                  operations work. Clean, intuitive interfaces that you don't need a manual to understand. 
                  Mobile-first design that works as well in the barn aisle as it does in the office.
                </p>
                <p className="text-white font-semibold">
                  BarnBills isn't just another software company. We're horse people building tools for horse people.
                </p>
              </div>
            </div>
            
            {/* Right Content */}
            <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-6">Our Mission</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 brand-gradient rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <p className="text-gray-300">Modernize horse farm management with intuitive, powerful tools</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 brand-gradient rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <p className="text-gray-300">Create software that doesn't require training manuals</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 brand-gradient rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <p className="text-gray-300">Support every type of horse operation, from backyard to enterprise</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 brand-gradient rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <p className="text-gray-300">Provide professional tools for professional operations</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Meet the Founder</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              BarnBills is led by someone who has lived and breathed the horse industry from day one.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 lg:p-12">
              <div className="grid lg:grid-cols-3 gap-8 items-center">
                {/* Profile Image Placeholder */}
                <div className="lg:col-span-1">
                  <div className="w-48 h-48 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <div className="text-4xl font-bold text-white">KP</div>
                  </div>
                </div>
                
                {/* Bio Content */}
                <div className="lg:col-span-2">
                  <h3 className="text-2xl font-bold text-white mb-2">Koby Pierce</h3>
                  <p className="text-blue-400 font-semibold mb-6">CEO & Founder</p>
                  
                  <div className="space-y-4 text-gray-400">
                    <p>
                      Born in Oklahoma and raised on horseback, Koby moved to Kentucky in 2019 where he's been 
                      working on his family's horse farm ever since. At just 16 years old, he's already built 
                      multiple successful ventures, but his passion for the equestrian industry runs deeper than business.
                    </p>
                    <p>
                      Having grown up immersed in daily farm operations, Koby witnessed firsthand how poorly 
                      horse farm owners were served by traditional billing and management software. The tools 
                      were outdated, confusing, and clearly built by people who didn't understand the unique 
                      challenges of running a modern horse operation.
                    </p>
                    <p>
                      Frustrated by the lack of quality options and armed with both technical expertise and 
                      deep industry knowledge, Koby decided to create something better. That vision became BarnBills – 
                      modern, intuitive software designed by horse people, for horse people.
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-4 mt-6">
                    <div className="flex items-center space-x-2 text-sm text-gray-400">
                      <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                      </svg>
                      <span>Kentucky</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-400">
                      <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"></path>
                      </svg>
                      <span>Lifelong horseman</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-400">
                      <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd"></path>
                      </svg>
                      <span>Serial entrepreneur</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Our Journey</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              From initial idea to beta testing, here's how BarnBills has evolved.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-purple-600"></div>
              
              <div className="space-y-12">
                {/* July 2024 */}
                <div className="relative flex items-start">
                  <div className="w-16 h-16 brand-gradient rounded-full flex items-center justify-center text-white font-bold text-sm mr-8 relative z-10">
                    Jul '24
                  </div>
                  <div className="flex-1 bg-gray-800 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-2">Idea Formation</h3>
                    <p className="text-gray-400">
                      After years of frustration with existing horse farm software, the vision for BarnBills 
                      was born. The goal: create modern, intuitive tools that actually understand horse operations.
                    </p>
                  </div>
                </div>

                {/* September 2024 */}
                <div className="relative flex items-start">
                  <div className="w-16 h-16 brand-gradient rounded-full flex items-center justify-center text-white font-bold text-sm mr-8 relative z-10">
                    Sep '24
                  </div>
                  <div className="flex-1 bg-gray-800 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-2">Design & UX Work Begins</h3>
                    <p className="text-gray-400">
                      Started designing the user experience and interface. Focus on mobile-first design and 
                      intuitive workflows that make sense to horse farm operators.
                    </p>
                  </div>
                </div>

                {/* April 2025 */}
                <div className="relative flex items-start">
                  <div className="w-16 h-16 brand-gradient rounded-full flex items-center justify-center text-white font-bold text-sm mr-8 relative z-10">
                    Apr '25
                  </div>
                  <div className="flex-1 bg-gray-800 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-2">Backend Development Starts</h3>
                    <p className="text-gray-400">
                      Development of the core dashboard and backend infrastructure begins. Building the foundation 
                      for automated billing, horse records, and multi-user functionality.
                    </p>
                  </div>
                </div>

                {/* May 2025 */}
                <div className="relative flex items-start">
                  <div className="w-16 h-16 brand-gradient rounded-full flex items-center justify-center text-white font-bold text-sm mr-8 relative z-10">
                    May '25
                  </div>
                  <div className="flex-1 bg-gray-800 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-2">LLC Registration</h3>
                    <p className="text-gray-400">
                      BarnBills LLC officially registered, marking the formal beginning of the company. 
                      Based on <a href="https://www.bizapedia.com/ky/barnbills-llc.html" className="text-blue-400 hover:text-blue-300">Bizapedia records</a>, 
                      establishing our legal foundation for growth.
                    </p>
                  </div>
                </div>

                {/* June 2025 */}
                <div className="relative flex items-start">
                  <div className="w-16 h-16 brand-gradient rounded-full flex items-center justify-center text-white font-bold text-sm mr-8 relative z-10">
                    Jun '25
                  </div>
                  <div className="flex-1 bg-gray-800 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-2">Early Beta Testing</h3>
                    <p className="text-gray-400">
                      First wave of beta testers begins using BarnBills. Real horse farm operators testing 
                      the platform and providing feedback to refine features and user experience.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Our Values</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              These principles guide everything we build and every decision we make.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Simplicity */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Simplicity Over Complexity</h3>
              <p className="text-gray-400">
                Every feature should be intuitive. If it requires a manual, we haven't done our job.
              </p>
            </div>

            {/* Industry Expertise */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v11"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Industry Expertise</h3>
              <p className="text-gray-400">
                Built by people who live and work in the horse industry every day.
              </p>
            </div>

            {/* Modern Technology */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Modern Technology</h3>
              <p className="text-gray-400">
                Built this century, for this century. Mobile-first, fast, and reliable.
              </p>
            </div>

            {/* Customer Success */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Customer Success</h3>
              <p className="text-gray-400">
                Your success is our success. We're here to help you thrive, not just sell software.
              </p>
            </div>

            {/* Continuous Improvement */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Continuous Improvement</h3>
              <p className="text-gray-400">
                We're never done. Always listening, always improving, always evolving.
              </p>
            </div>

            {/* Transparency */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Transparency</h3>
              <p className="text-gray-400">
                Clear pricing, honest communication, and no hidden surprises. Ever.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Experience the Difference?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Join us in revolutionizing horse farm management. Built by horse people, for horse people.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setIsModalOpen(true)}
              className="brand-gradient text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
            >
              Get Notified at Launch
            </button>
            <Link 
              to="/features" 
              className="border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200"
            >
              Explore Features
            </Link>
          </div>
        </div>
      </section>

      <LaunchModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default AboutPage; 